import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Alert } from '@/components/ui/Alert/Alert';
import { LocationAutocomplete, type LocationData } from '@/components/ui/LocationAutocomplete/LocationAutocomplete';
import { VStack, HStack } from '@/components/ui/Stack';
import { MapPin, ArrowRightLeft, Plus, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DestinationOption {
  id: string;
  city: string;
  country: string;
}

export interface MoveToDestinationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMove: (destinationId: string) => Promise<void>;
  onCreateAndMove?: (destination: { city: string; country: string; place_id?: string; destination_type?: 'city' | 'region' | 'country' }) => Promise<void>;
  currentDestinationId?: string;
  destinations: DestinationOption[];
  venueName: string;
  allowCreateNew?: boolean;
  isMoving?: boolean;
  /** Pre-fill values for creating new destination (e.g., from geocoded import) */
  suggestedNewDestination?: { city: string; country: string };
}

/**
 * Modal for moving a venue to a different destination
 * Used in Step 1 (Shortlist) and Step 3 (Selection)
 */
export function MoveToDestinationModal({
  isOpen,
  onClose,
  onMove,
  onCreateAndMove,
  currentDestinationId,
  destinations,
  venueName,
  allowCreateNew = true,
  isMoving = false,
  suggestedNewDestination,
}: MoveToDestinationModalProps) {
  const [selectedDestinationId, setSelectedDestinationId] = useState<string | null>(null);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newLocationSearch, setNewLocationSearch] = useState('');
  const [newDestination, setNewDestination] = useState<{
    city: string;
    country: string;
    place_id?: string;
    destination_type?: 'city' | 'region' | 'country';
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // If there's a suggested new destination (no match found), show create mode
      if (suggestedNewDestination && allowCreateNew) {
        setShowCreateNew(true);
        setNewDestination({
          city: suggestedNewDestination.city,
          country: suggestedNewDestination.country,
        });
        setNewLocationSearch(`${suggestedNewDestination.city}, ${suggestedNewDestination.country}`);
        setSelectedDestinationId(null);
      } else {
        setSelectedDestinationId(null);
        setShowCreateNew(false);
        setNewLocationSearch('');
        setNewDestination(null);
      }
      setError(null);
    }
  }, [isOpen, suggestedNewDestination, allowCreateNew]);

  function handleClose() {
    if (!isMoving) {
      setSelectedDestinationId(null);
      setShowCreateNew(false);
      setNewLocationSearch('');
      setNewDestination(null);
      setError(null);
      onClose();
    }
  }

  function handleLocationSelect(location: LocationData) {
    const isCountry = location.location_type === 'country';
    const isRegion = location.location_type === 'region';

    // For regions (states/provinces), use region name as the city field for display
    const cityName = isCountry
      ? location.country || location.formatted_address.split(',')[0].trim()
      : isRegion
        ? location.region || location.formatted_address.split(',')[0].trim()
        : location.city || location.formatted_address.split(',')[0].trim();
    const countryName = isCountry ? cityName : (location.country || 'Unknown');

    if (!cityName || !countryName) {
      setError('Invalid location selected. Please try again.');
      return;
    }

    // Check if this destination already exists
    const exists = destinations.some(
      d => d.city.toLowerCase() === cityName.toLowerCase() &&
           d.country.toLowerCase() === countryName.toLowerCase()
    );

    if (exists) {
      setError('This destination already exists. Please select it from the list above.');
      setNewDestination(null);
      return;
    }

    setNewDestination({
      city: cityName,
      country: countryName,
      place_id: location.place_id,
      destination_type: isCountry ? 'country' : isRegion ? 'region' : 'city',
    });
    setError(null);
  }

  async function handleConfirm() {
    setError(null);

    try {
      if (showCreateNew && newDestination && onCreateAndMove) {
        await onCreateAndMove(newDestination);
      } else if (selectedDestinationId) {
        await onMove(selectedDestinationId);
      } else {
        setError('Please select a destination or create a new one');
        return;
      }
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to move venue');
    }
  }

  function handleSelectDestination(destId: string) {
    setSelectedDestinationId(destId);
    setShowCreateNew(false);
    setNewDestination(null);
    setNewLocationSearch('');
    setError(null);
  }

  function handleSwitchToCreate() {
    setShowCreateNew(true);
    setSelectedDestinationId(null);
    setError(null);
  }

  // Filter out current destination
  const availableDestinations = destinations.filter(d => d.id !== currentDestinationId);
  const canConfirm = (showCreateNew && newDestination) || selectedDestinationId;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      title="Move to Different Destination"
      description={`Select where to move "${venueName}"`}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isMoving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!canConfirm || isMoving}
            isLoading={isMoving}
            leftIcon={<ArrowRightLeft className="w-4 h-4" />}
          >
            {showCreateNew ? 'Create & Move' : 'Move Venue'}
          </Button>
        </>
      }
    >
      <VStack gap={4}>
        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </Alert>
        )}

        {/* Destination List */}
        {availableDestinations.length > 0 && (
          <div>
            <label className="text-sm font-medium mb-2 block">
              Select Existing Destination
            </label>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {availableDestinations.map((dest) => (
                <button
                  key={dest.id}
                  type="button"
                  onClick={() => handleSelectDestination(dest.id)}
                  disabled={isMoving}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left',
                    selectedDestinationId === dest.id
                      ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                    isMoving && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <div className={cn(
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                    selectedDestinationId === dest.id
                      ? 'border-primary-500 bg-primary-500'
                      : 'border-gray-300'
                  )}>
                    {selectedDestinationId === dest.id && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <MapPin className={cn(
                    'w-4 h-4 flex-shrink-0',
                    selectedDestinationId === dest.id ? 'text-primary-600' : 'text-gray-400'
                  )} />
                  <div className="flex-1 min-w-0">
                    <span className={cn(
                      'font-medium',
                      selectedDestinationId === dest.id ? 'text-primary-900' : 'text-gray-900'
                    )}>
                      {dest.city === dest.country ? dest.country : dest.city}
                    </span>
                    {dest.city !== dest.country && (
                      <span className="text-gray-500 ml-1">, {dest.country}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No destinations message */}
        {availableDestinations.length === 0 && !allowCreateNew && (
          <div className="text-center py-6 text-gray-500">
            <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No other destinations available</p>
          </div>
        )}

        {/* Create New Destination Option */}
        {allowCreateNew && onCreateAndMove && (
          <>
            {availableDestinations.length > 0 && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>
            )}

            {!showCreateNew ? (
              <Button
                variant="outline"
                onClick={handleSwitchToCreate}
                disabled={isMoving}
                leftIcon={<Plus className="w-4 h-4" />}
                className="w-full"
              >
                Create New Destination
              </Button>
            ) : (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="text-sm font-medium mb-2 block">
                  Create New Destination
                </label>
                <LocationAutocomplete
                  value={newLocationSearch}
                  onChange={setNewLocationSearch}
                  onLocationSelect={handleLocationSelect}
                  placeholder="Search for a city, state, or country..."
                  types="cities"
                  disabled={isMoving}
                />
                
                {newDestination && (
                  <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
                    <MapPin className="w-4 h-4 text-primary-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-primary-900">
                      {newDestination.city === newDestination.country 
                        ? newDestination.country 
                        : `${newDestination.city}, ${newDestination.country}`}
                    </span>
                  </div>
                )}
                
                {availableDestinations.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateNew(false);
                      setNewDestination(null);
                      setNewLocationSearch('');
                    }}
                    className="mt-2 text-sm text-gray-500 hover:text-gray-700"
                    disabled={isMoving}
                  >
                    ← Back to existing destinations
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* Info box for Cvent import flow */}
        {suggestedNewDestination && (
          <div className="p-3 bg-info-50 border border-info-200 rounded-lg">
            <p className="text-sm text-info-900">
              <strong>Note:</strong> The venue's city "{suggestedNewDestination.city}" doesn't match any existing destinations. 
              You can either select an existing destination or create a new one.
            </p>
          </div>
        )}
      </VStack>
    </Modal>
  );
}

